import type { LucideIcon } from 'lucide-react'
import type { CategoryPerformanceStats } from '@/types/miniApp'

const COLOR_CLASSES: Record<string, { text: string; from: string; border: string }> = {
  green: { text: 'text-emerald-400', from: 'from-emerald-950/40', border: 'border-emerald-500/40' },
  blue: { text: 'text-sky-400', from: 'from-sky-950/40', border: 'border-sky-500/40' },
  purple: { text: 'text-purple-400', from: 'from-purple-950/40', border: 'border-purple-500/40' },
  orange: { text: 'text-orange-400', from: 'from-orange-950/40', border: 'border-orange-500/40' },
  red: { text: 'text-red-400', from: 'from-red-950/40', border: 'border-red-500/40' },
  // P0.26 (Sección 6 del encargo): Same Game Parlay -- mismo token `violet` ya usado en
  // ParlaysPage.tsx para su branding (nunca un morado genérico de Tailwind sin relación).
  violet: { text: 'text-violet', from: 'from-violet/10', border: 'border-violet/40' },
}

/** Fase B (06/08/2026) -- versión Mini App de la tarjeta genérica de estrategia (mismos
 * campos que `frontend/.../StrategyPerformanceCard.tsx`), datos reales, nunca "0%" sin muestra. */
export function StrategyPerformanceCard({
  icon: Icon,
  label,
  color,
  stats,
}: {
  icon: LucideIcon
  label: string
  color: string
  stats?: CategoryPerformanceStats | null
}) {
  const c = COLOR_CLASSES[color] ?? COLOR_CLASSES.green
  const hasSample = Boolean(stats && stats.decided_picks > 0)
  const fmtPct = (v: number | null | undefined) => (hasSample && v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(1)}%` : 'N/D')

  return (
    <div className={`p-4 rounded-2xl border shadow-md space-y-3 bg-gradient-to-br ${c.from} via-panel to-panel ${c.border}`}>
      <div className="flex items-center justify-between border-b border-border/40 pb-2">
        <h3 className={`text-xs font-black uppercase tracking-wider ${c.text} flex items-center gap-1.5`}>
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
        </h3>
        {!hasSample && (
          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-panel-elevated text-text-dim border border-border/50">
            Sin datos
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Metric label="Win Rate" value={hasSample && stats?.win_rate != null ? `${stats.win_rate.toFixed(1)}%` : 'N/D'} accent={c.text} />
        <Metric label="ROI" value={fmtPct(stats?.roi)} accent={c.text} />
        <Metric label="Yield" value={fmtPct(stats?.yield_pct)} accent={c.text} />
        <Metric label="Profit Factor" value={hasSample && stats?.profit_factor != null ? stats.profit_factor.toFixed(2) : 'N/D'} accent={c.text} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs font-mono pt-1">
        <MiniStat label="Liquidados" value={String(stats?.settled_picks ?? 0)} />
        <MiniStat label="G / P" value={`${stats?.wins ?? 0} / ${stats?.losses ?? 0}`} />
        <MiniStat label="Cuota Med." value={hasSample && stats?.average_odds != null ? `@${stats.average_odds.toFixed(2)}` : 'N/D'} />
      </div>
    </div>
  )
}

function Metric({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
      <span className="text-[9px] font-bold uppercase tracking-wider text-text-dim block">{label}</span>
      <span className={`text-lg font-mono font-black ${accent}`}>{value}</span>
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-panel-elevated/30 p-2 rounded-lg">
      <span className="text-[9px] font-bold text-text-dim block">{label}</span>
      <span className="font-black text-text">{value}</span>
    </div>
  )
}
